import { Link } from 'react-router-dom';
import { CheckCircle2, Clock, XCircle } from 'lucide-react';
import { Button, Card } from '../../PrizeDraw/components/ui';
import type { SkillTestTerminalVariant } from '../skillTestUtils';
import { SkillTestSuccessState } from './SkillTestSuccessState';

type TerminalStateProps = {
  variant: SkillTestTerminalVariant;
};

const terminalContent: Record<
  Exclude<SkillTestTerminalVariant, 'success'>,
  {
    icon: typeof CheckCircle2;
    iconClassName: string;
    title: string;
    message: string;
  }
> = {
  failed: {
    icon: XCircle,
    iconClassName: 'bg-[#FEE2E2] text-[#DC2626]',
    title: 'Out of attempts',
    message: 'You used all available attempts without a correct answer, so you are not eligible to proceed.',
  },
  expired: {
    icon: Clock,
    iconClassName: 'bg-[#FEF3C7] text-[#D97706]',
    title: 'Time expired',
    message: 'The timer ran out before a correct answer was submitted, so you are not eligible to proceed.',
  },
  already_completed: {
    icon: CheckCircle2,
    iconClassName: 'bg-[#EEF4FF] text-[#0055FF]',
    title: 'Skill test already completed',
    message: 'This skill test link has already been used and cannot be taken again.',
  },
  invalid_token: {
    icon: XCircle,
    iconClassName: 'bg-[#FEE2E2] text-[#DC2626]',
    title: "This link isn't valid",
    message: 'The skill test link you opened is invalid or no longer recognized.',
  },
  contest_closed: {
    icon: Clock,
    iconClassName: 'bg-[#FEF3C7] text-[#D97706]',
    title: 'Contest closed',
    message: 'This contest skill test has expired or the contest is no longer open.',
  },
};

export function TerminalState({ variant }: TerminalStateProps) {
  if (variant === 'success') {
    return <SkillTestSuccessState />;
  }

  const content = terminalContent[variant];
  const Icon = content.icon;

  return (
    <Card className='p-8 text-center shadow-[0_10px_30px_rgba(0,0,0,0.08)]'>
      <div
        className={`mx-auto mb-5 flex h-[64px] w-[64px] items-center justify-center rounded-full ${content.iconClassName}`}
      >
        <Icon className='h-8 w-8' aria-hidden='true' />
      </div>
      <h1 className='mb-2 font-display text-[24px] font-semibold text-[#0A0B14]'>{content.title}</h1>
      <p className='mx-auto max-w-[360px] text-[15px] leading-6 text-[#5B6478]'>{content.message}</p>
      <div className='mt-6'>
        <Link to='/login'>
          <Button variant='secondary'>Back to home</Button>
        </Link>
      </div>
    </Card>
  );
}
